import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, AlertTriangle, Ban } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { StatusBadge, CriticalityBadge } from "@/features/assets/badges";
import { formatDate, warrantyState } from "@/features/assets/format";
import { getAssetPublic, type PublicAssetView } from "@/lib/asset-public.functions";

const HELPDESK = "0484-XXXXXXX";
const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

async function fetchPublicAsset(code: string): Promise<PublicAssetView> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/assets/public/${encodeURIComponent(code)}/`);
    if (!res.ok) return null;
    const asset = await res.json();
    return {
      id: asset.id,
      asset_code: asset.asset_code,
      name: asset.name,
      status: asset.status,
      criticality: asset.criticality,
      allocation_status: asset.allocation_status,
      warranty_end_date: asset.warranty_end_date ?? null,
      warranty_start_date: asset.warranty_start_date ?? null,
      branch_name: asset.branch_name ?? null,
      location_name: asset.location_name ?? null,
      department_name: asset.department_name ?? null,
    };
  } catch {
    return null;
  }
}

export const Route = createFileRoute("/asset/$")({
  ssr: false,
  component: PublicAssetPage,
  head: () => ({
    meta: [{ title: `Asset — HAMS` }],
  }),
});

function PublicAssetPage() {
  const { _splat } = Route.useParams();
  const raw = _splat ?? "";
  const decodedCode = (() => { try { return decodeURIComponent(raw); } catch { return raw; } })();
  const encodedCode = encodeURIComponent(decodedCode);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["public-asset", decodedCode],
    queryFn: () => fetchPublicAsset(decodedCode),
  });

  async function handleTicket() {
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (isAuthenticated && data?.id) {
      navigate({
        to: "/assets/$assetId/report-issue",
        params: { assetId: data.id },
        search: { source: "QR" } as never,
      });
    } else {
      navigate({ to: "/auth", search: { next: `/asset/${encodedCode}` } as never });
    }
  }

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6">
      <div className="mx-auto w-full max-w-[480px] space-y-4">
        <header className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">H</div>
          <h2 className="mt-2 text-sm font-semibold tracking-tight text-muted-foreground">HAMS — Hospital Asset Management</h2>
        </header>

        {isLoading ? (
          <Card className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Card>
        ) : !data ? (
          <Card className="p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
            <h1 className="mt-3 text-lg font-semibold">Asset not found</h1>
            <p className="mt-1 text-sm text-muted-foreground">Please contact the biomedical department.</p>
            <a href={`tel:${HELPDESK}`} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"><Phone className="h-4 w-4" />Call Biomedical: {HELPDESK}</a>
          </Card>
        ) : (
          <AssetCard asset={data} onTicket={handleTicket} />)}

        <footer className="pt-2 text-center text-xs text-muted-foreground">HAMS | GKS Infotech</footer>
      </div>
    </div>
  );
}

function AssetCard({ asset, onTicket }: { asset: NonNullable<Awaited<ReturnType<typeof getAssetPublic>>>; onTicket: () => void }) {
  const decommissioned = asset.status === "Condemned" || asset.status === "Disposed";
  const wState = warrantyState(asset.warranty_end_date);
  const hasWarranty = wState !== "none" && wState !== "expired";

  return (
    <>
      {decommissioned && (
        <Card className="border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span><strong>This asset has been decommissioned.</strong> Do not use.</span>
          </div>
        </Card>
      )}

      <Card className="space-y-4 p-5">
        <div>
          <h1 className="text-2xl font-bold leading-tight">{asset.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">{asset.asset_code}</Badge>
            <StatusBadge value={asset.status} />
            <CriticalityBadge value={asset.criticality} />
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-2 text-sm">
          <Row label="Department" value={asset.department_name ?? "—"} />
          <Row label="Location" value={asset.location_name ?? "—"} />
          <Row label="Branch" value={asset.branch_name ?? "—"} />
          <Row label="Last PM" value={<span className="text-muted-foreground">Not recorded</span>} />
          <Row label="Next PM Due" value={<span className="text-muted-foreground">Not scheduled</span>} />
          <Row label="Calibration" value={<span className="text-muted-foreground">N/A</span>} />
          <Row
            label="AMC / Warranty"
            value={
              hasWarranty ? (
                <span className={wState === "expiring" ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>
                  Yes — until {formatDate(asset.warranty_end_date)}
                </span>
              ) : wState === "expired" ? (
                <span className="text-red-600 font-medium">Expired ({formatDate(asset.warranty_end_date)})</span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )
            }
          />
        </dl>

        <Button
          onClick={onTicket}
          disabled={decommissioned}
          className="h-14 w-full bg-red-600 text-white text-base font-semibold hover:bg-red-700"
        >
          <Ban className="mr-2 h-5 w-5" /> Report Issue
        </Button>

        <a href={`tel:${HELPDESK}`} className="flex items-center justify-center gap-2 rounded-md border bg-background py-3 text-sm font-medium">
          <Phone className="h-4 w-4" /> Call Biomedical: {HELPDESK}
        </a>
      </Card>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b pb-2 last:border-0 last:pb-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

